import React from 'react'
import { GetServerSideProps, NextPage } from 'next'
import { ArticlePage } from 'experiences/learn/articles/ArticlePage'
import { getSeoMetaObjectFromArticle } from 'lib/routing/MetaTags'
import { Article } from 'services/ContentfulService/models/articles'
import { getArticleBySlug } from 'services/ContentfulService/hooks/articles'
import { NamedPromises } from 'lib'
import { getTopics } from 'services/ContentfulService/hooks/topics'
import { FeaturedConstraint } from 'services/ContentfulService/constraints'
import { Topic } from 'services/ContentfulService/models/topics'
import { getServerSidePropsWrapper } from 'lib/routing/nextPageUtils'
import { StandardLayout } from 'shared/layout/StandardLayout'
import { getDisclaimer } from 'services/ContentfulService/hooks/resources'
import { SingleResource } from 'services/ContentfulService/models/resource/resource'
import { enhanceAsset } from 'services/ContentfulService/models/asset'

interface Props {
	article: Article
	disclaimer: SingleResource
	topics: Topic[]
}

const ArticleRoute: NextPage<Props> = ({ article, disclaimer, topics }): JSX.Element => (
	<StandardLayout metaTags={getSeoMetaObjectFromArticle(article)}>
		<ArticlePage article={article} disclaimer={disclaimer} featuredTopics={topics} />
	</StandardLayout>
)

export const getServerSideProps: GetServerSideProps = getServerSidePropsWrapper<Props>(async ctx => {
	const slug = ctx.query.id as string
	const article = getArticleBySlug(slug).then(article =>
		enhanceAsset(article.primaryImage).then(asset => ({ ...article, primaryImage: asset }))
	)
	const disclaimer = getDisclaimer()
	const featuredTopics = getTopics({ constraints: [FeaturedConstraint()], count: 4 })
	const result = await NamedPromises({
		article: article,
		disclaimer: disclaimer,
		topics: featuredTopics
	})
	const dedupedTopics = [...result.article.topics, ...result.topics].reduce((acc, topic) => {
		return acc.some(t => t.id === topic.id) ? acc : [...acc, topic]
	}, [] as Topic[])
	dedupedTopics.forEach(topic => {
		topic.faqs = []
		topic.shallow = true
	})
	return {
		...result,
		topics: dedupedTopics
	}
})

export default ArticleRoute
